import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import UtilService from "../../../services/UtilService";

const TopicService = {
    requestList: async (paging, sort, setRows, setRowCount, keyword, selectedGenre, selectedTags = []) => {
        let param = {
            page: paging.page,
            pageSize: paging.pageSize,
            sort: sort.map((s) => `${s.field}:${s.sort}`).join(","),
            keyword: keyword ? encodeURIComponent(keyword) : "",
            genreId: selectedGenre ? selectedGenre.id : "",
        };
        if (selectedTags && Array.isArray(selectedTags) && selectedTags.length > 0) {
            const tagStrings = selectedTags.map((tag) => (typeof tag === "string" ? tag : tag?.tag ?? String(tag)).toLowerCase().trim()).filter((t) => t.length > 0);
            if (tagStrings.length > 0) {
                param.tags = tagStrings.join(",");
            }
        }
        // Logger.log(param);

        const result = await ApiService.getData("/v1/topic", param);
        Logger.log(result);

        setRows(result.content || []);
        setRowCount(result.page?.totalElements ?? 0);

        // Logger.log(result);
    },

    listTopics: async (page = 0, pageSize = 10, sort = "createdAt:desc", options = {}) => {
        try {
            const params = {
                page,
                pageSize,
                sort
            };
            if (options.keyword != null && options.keyword !== "") params.keyword = options.keyword;
            if (options.genreId != null && options.genreId !== "") params.genreId = options.genreId;
            if (options.bedrockIdeaId != null && options.bedrockIdeaId !== "") params.bedrock_idea_id = options.bedrockIdeaId;
            if (options.tags != null && Array.isArray(options.tags) && options.tags.length > 0) {
                params.tags = options.tags.map((t) => (typeof t === "string" ? t : t?.tag ?? String(t)).toLowerCase().trim()).filter((t) => t.length > 0).join(",");
            }
            const response = await ApiService.getData("/v1/topic", params);
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error listing topics:", error);
            return { success: false, error: error.message || "Failed to fetch topics" };
        }
    },

    requestTopicTags: async () => {
        try {
            const result = await ApiService.getData("/v1/topic/distinct-tags");
            if (!Array.isArray(result) || result.length === 0) return [];
            return result.map((item) => {
                if (typeof item === "string") return item;
                return item?.tag ?? item?.name ?? item?.value ?? (item && Object.values(item)[0]) ?? String(item);
            }).filter((t) => t && String(t).trim().length > 0);
        } catch (error) {
            Logger.error("Error fetching topic tags:", error);
            return [];
        }
    },

    requestTopic: async (topicId) => {
        const result = await ApiService.getData(`/v1/topic/${topicId}`);

        // Logger.log(result);
        return result;
    },

    updateTopic: async (topicId, title, relatedWords) => {
        let relatedWordsArray = relatedWords.split("\n");

        // Remove duplicates by converting the array to a Set and back to an array
        relatedWordsArray = [...new Set(relatedWordsArray)];

        // Filter out empty strings
        relatedWordsArray = relatedWordsArray.filter((word) => word.trim() !== "");

        // Log the cleaned relatedWords
        // Logger.log(relatedWordsArray.join("\n"));

        let param = {
            topicId: topicId,
            title: title,
            relatedWords: relatedWordsArray.join("\n"),
        };

        // Logger.log(relatedWords);

        const result = await ApiService.putData("/v1/topic", param);
        return result;
    },

    deleteTopic: async (topicId, force = false) => {
        const url = force ? `/v1/topic/${topicId}?force=true` : `/v1/topic/${topicId}`;
        const result = await ApiService.deleteData(url);
        return result;
    },

    createTopic: async (title, relatedWords) => {
        let param = {
            title,
            relatedWords,
        };
        // Logger.log(relatedWords);

        const result = await ApiService.postData("/v1/topic", param);
        return result;
    },

    fetchNewsFromGuardian: async (keyword) => {
        // Get your free Guardian API key at: https://open-platform.theguardian.com/
        // Free tier allows 500 requests per day
        const API_KEY = "0c98f86f-328d-40ae-8761-c46c3bd398a7"; // Replace with your actual Guardian API key

        if (!keyword || !keyword.trim()) {
            throw new Error("Please enter a keyword to search");
        }

        try {
            const response = await fetch(
                `https://content.guardianapis.com/search?q=${encodeURIComponent(keyword)}&show-fields=all&show-tags=all&api-key=${API_KEY}`
            );

            if (!response.ok) {
                throw new Error("Failed to fetch news from Guardian API");
            }

            const data = await response.json();

            // Transform Guardian API response to match our component structure
            const transformedResults = (data.response.results || []).map(article => ({
                title: article.webTitle,
                description: article.fields?.trailText || article.fields?.headline || "",
                content: article.fields?.bodyText || article.fields?.body || "",
                link: article.webUrl,
                image_url: article.fields?.thumbnail,
                source_id: article.sectionName,
                pubDate: article.webPublicationDate,
                keywords: article.tags?.map(tag => tag.webTitle) || []
            }));

            return transformedResults;
        } catch (error) {
            throw new Error("Error fetching news from Guardian API: " + error.message);
        }
    },

    fetchNews: async (keyword) => {
        const API_KEY = "pub_1317b22531b84c4c9a1d861d6ee27aec";

        if (!keyword || !keyword.trim()) {
            throw new Error("Please enter a keyword to search");
        }

        try {
            const response = await fetch(
                `https://newsdata.io/api/1/news?apikey=${API_KEY}&q=${encodeURIComponent(keyword)}&language=en`
            );

            if (!response.ok) {
                throw new Error("Failed to fetch news");
            }

            const data = await response.json();
            return data.results || [];
        } catch (error) {
            throw new Error("Error fetching news: " + error.message);
        }
    },

    fetchNewsFromCurrents: async (keyword) => {
        const API_KEY = "B1-hD_1Tjc1_RUBLC-SUEBCNjEKz4V7Y6xAI8xHCzvHsG_MC";

        if (!keyword || !keyword.trim()) {
            throw new Error("Please enter a keyword to search");
        }

        try {
            const response = await fetch(
                `https://api.currentsapi.services/v1/search?apiKey=${API_KEY}&keywords=${encodeURIComponent(keyword)}&language=en`
            );

            if (!response.ok) {
                throw new Error("Failed to fetch news from Current API");
            }

            const data = await response.json();

            // Transform currentsapi response to match our component structure
            const transformedResults = (data.news || []).map(article => ({
                title: article.title,
                description: article.description,
                content: article.content,
                link: article.url,
                image_url: article.image,
                source_id: article.author,
                pubDate: article.published,
                keywords: article.category ? [article.category] : []
            }));

            return transformedResults;
        } catch (error) {
            throw new Error("Error fetching news from Current API: " + error.message);
        }
    },

    extractContent: async (content) => {
        try {
            const result = await ApiService.putData("/v1/topic/extract", { content: content });
            Logger.log(result);

            return result;

        } catch (error) {
            throw new Error("Error extracting content: " + error.message);
        }
    },

    requestTopGenres: async () => {
        const result = await ApiService.getData("/v1/genre", {
            active: true,
            rootOnly: true
        });
        return result;
    },

    cleanupUnrelatedTopics: async () => {
        const result = await ApiService.deleteData("/v1/topic/cleanup");
        return result;
    },
};

export default TopicService;
